import {
  useEffect, useMemo, useRef, useState
} from 'react';
import useSWR from 'swr';

import get from 'lodash/get';

import {
  fillInMissingPoints, filterExcessZeroes, transformResultsIntoValues
} from '../chart_utils/chart_utils';

import { shouldDisablePrometheus, sinatraOrigin } from '../../config';
import keycloakService from '../../keycloak_service';

const fetchPrometheusQuery = (queryParams) => {
  const [query, startTimestamp, endTimestamp, step] = queryParams;

  if (shouldDisablePrometheus) {
    throw new Error('Prometheus is disabled.');
  }

  if (!query) {
    throw new Error('No query provided.');
  }

  return fetch(
    `${sinatraOrigin}/v1/metrics/api/v1/query_range?query=${encodeURIComponent(
      query,
    )}&start=${startTimestamp}&end=${endTimestamp}&step=${step}`,
    { headers: { Authorization: keycloakService.getAuthHeader() } }
  )
    .then(response => response.json())
    .then((response) => {
      if (response && response.status === 'success') {
        const resultSets = get(response, 'data.result', []);
        const values = transformResultsIntoValues(resultSets);

        if (!values) {
          throw new Error("Couldn't load Prometheus data");
        }

        const normalizedResponse = filterExcessZeroes(fillInMissingPoints(values, startTimestamp, endTimestamp));

        return { values, normalizedResponse };
      }

      throw new Error("Couldn't load Prometheus data")
    })
}

// TODO - Query every step seconds, and transpose latest value otherwise.
// TODO - when an update response is received - only post the new timestamps (take a diff of the timestamps)
// TODO - For new datasets - query every second until data is detected
// TODO - When step value or range is changed - treat as new dataset
// todo - Clear chart values for new datasets
const usePrometheusQuery = (query, {
  startTimestamp: unalignedStartTime,
  endTimestamp,
  step,
  transformData = data => data,
  validationDelay = 2000,
}) => {
  const timeoutRef = useRef(null);
  const errorTimeoutRef = useRef(null);

  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setError] = useState(null);
  const [currentData, setCurrentData] = useState([]);

  const startTimestamp = unalignedStartTime - (unalignedStartTime % step);
  const { data, error, isValidating } = useSWR([query, startTimestamp, endTimestamp, step], fetchPrometheusQuery);

  const transformedData = useMemo(() => transformData(currentData), [currentData, transformData]);

  useEffect(() => {
    if (data && data?.normalizedResponse) {
      setCurrentData(data.normalizedResponse);
      setLoading(false);
    }
  }, [data])

  useEffect(() => {
    setLoading(true);
  }, [step])

  useEffect(() => {
    if (isValidating) {
      timeoutRef.current = setTimeout(() => {
        setLoading(true);
      }, validationDelay);
    } else {
      clearTimeout(timeoutRef.current);
      setLoading(false);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    }
  }, [isValidating, validationDelay]);

  useEffect(() => {
    if (error) {
      errorTimeoutRef.current = setTimeout(() => {
        setError(error?.message);
      }, validationDelay);
    } else {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }

    return () => {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }
  }, [error, validationDelay]);

  return {
    data: transformedData,
    error: errorMessage,
    isLoading,
  }
}

export default usePrometheusQuery;
